// ============================================================================
// @jeraisonne/db — clue-hunt : helpers PURS du player "chasse aux indices"
// ============================================================================
// Modèle : un bloc clue_hunt contient N indices. Un chrono UNIQUE partagé
// (timeout_ms) redémarre à chaque validation. Ordre de découverte libre.
// Chaque mauvais clic sur un mot NON-cible divise par 2 la récompense restante
// de TOUS les indices encore non révélés (plancher 1). Au timeout, on révèle
// automatiquement l'indice non trouvé le plus proche (= premier de la liste
// non révélé) SANS XP.
//
// v1 : scoring CLIENT (pas d'anti-triche serveur). À durcir plus tard via une
// edge fn `submit-clue-hunt` + omission de `revealed_md`/`xp_reward` dans la
// RPC de gating `get_exercise_v2_for_student`. Cf. CLAUDE.md piège dédié.
// ============================================================================

export interface Clue {
  id: string;
  source_text: string;
  source_block_id?: string;
  revealed_md: string;
  xp_reward: number;
}

export interface ClueHuntData {
  intro_md: string;
  /** Chrono partagé (ms). Défaut applicatif : 30000. */
  timeout_ms: number;
  clues: Clue[];
}

/** État interne du player (côté client). Aucune écriture DB avant la fin. */
export interface ClueHuntState {
  /** Nb TOTAL de mauvais clics (impacte la récompense des indices restants). */
  wrongClicks: number;
  /** Nb de mauvais clics CONSÉCUTIFS (reset à chaque bon clic). Au 3e, on
      révèle automatiquement l'indice suivant sans XP (comme un timeout). */
  consecutiveWrongClicks: number;
  /** clue.id → true si révélé (par clic OU timeout OU 3 mauvais clics). */
  revealed: Set<string>;
  /** clue.id → XP effectivement crédités (0 = timeout / 3 mauvais clics). */
  earned: Map<string, number>;
}

/** Seuil de mauvais clics consécutifs déclenchant la révélation auto. */
export const CLUE_HUNT_WRONG_CLICKS_THRESHOLD = 3;

/**
 * Facteur d'échelle appliqué à la récompense de base des indices — aligné sur
 * l'échelle XP ×10 de toute l'app (cf. `@jeraisonne/ui` `BASE_XP_PER_CORRECT`).
 * Les `xp_reward` restent authored à petite échelle (1..40) ; le player les met
 * à l'échelle au moment de créditer.
 */
export const CLUE_XP_SCALE = 10;

/**
 * Plancher du facteur de vitesse : un indice trouvé JUSTE avant le timeout
 * rapporte encore `CLUE_TIME_FACTOR_FLOOR` de sa récompense restante (jamais 0
 * tant qu'il est trouvé activement — seuls le timeout et les 3 mauvais clics
 * donnent 0).
 */
export const CLUE_TIME_FACTOR_FLOOR = 0.4;

/** Récompense restante d'UN indice compte tenu des mauvais clics globaux. */
export function computeRemaining(baseReward: number, wrongClicks: number): number {
  if (baseReward <= 0) return 0;
  if (wrongClicks <= 0) return baseReward;
  // Division par 2 à chaque mauvais clic, plancher 1.
  const raw = Math.floor(baseReward * Math.pow(0.5, wrongClicks));
  return Math.max(1, raw);
}

/**
 * Facteur de vitesse (∈ [CLUE_TIME_FACTOR_FLOOR, 1]) selon le temps écoulé
 * depuis le dernier reset du chrono partagé. Continu (« plus précis ») :
 *   - `elapsedMs = 0`        -> 1 (plein) ;
 *   - `elapsedMs >= timeoutMs` -> `CLUE_TIME_FACTOR_FLOOR` ;
 *   - entre -> interpolation linéaire.
 *
 * `elapsedMs`/`timeoutMs` omis (ou timeout invalide) -> 1 (aucune décote).
 */
export function clueTimeFactor(elapsedMs?: number, timeoutMs?: number): number {
  if (elapsedMs == null || timeoutMs == null || timeoutMs <= 0) return 1;
  const frac = Math.min(1, Math.max(0, elapsedMs / timeoutMs));
  return 1 - (1 - CLUE_TIME_FACTOR_FLOOR) * frac;
}

/**
 * Récompense EFFECTIVE (≥ 1) d'un indice trouvé activement : récompense de base
 * amputée des mauvais clics (halving), mise à l'échelle ×10, puis pondérée par
 * la vitesse (plus on trouve vite, plus l'XP est élevée).
 */
export function computeClueReward(
  baseReward: number,
  wrongClicks: number,
  elapsedMs?: number,
  timeoutMs?: number,
): number {
  const remaining = computeRemaining(baseReward, wrongClicks);
  if (remaining <= 0) return 0;
  const scaled = remaining * CLUE_XP_SCALE * clueTimeFactor(elapsedMs, timeoutMs);
  return Math.max(1, Math.round(scaled));
}

/**
 * Retourne le premier indice non révélé (= "l'indice le plus proche" à
 * dévoiler au timeout). null si tout est déjà révélé.
 */
export function nextClueOnTimeout(
  clues: readonly Clue[],
  revealed: ReadonlySet<string>,
): Clue | null {
  for (const c of clues) if (!revealed.has(c.id)) return c;
  return null;
}

/**
 * Cherche un indice dont `source_text` matche le clic. La comparaison est
 * insensible à la casse et aux espaces internes (normalizeClueMatch), et
 * accepte les deux directions :
 *   - `clickText` égal à `source_text` (cas normal, texte en clair) ;
 *   - `clickText` CONTIENT `source_text` (cas KaTeX : le clic sur une
 *     formule renvoie tout le raw LaTeX, ex. `P_H(t) = 6{,}9 \\times t`,
 *     alors que le source_text peut être juste `6{,}9 \\times t`) ;
 *   - `source_text` contient `clickText` (cas où le user clique sur un
 *     mot inclus dans un indice plus long — moins courant mais utile).
 *
 * Ne considère QUE les indices pas encore révélés. Renvoie l'indice à révéler,
 * ou null (mauvais clic).
 */
export function findClueByClick(
  clickText: string,
  clues: readonly Clue[],
  revealed: ReadonlySet<string>,
): Clue | null {
  const needle = normalizeClueForFind(clickText);
  if (!needle) return null;
  for (const c of clues) {
    if (revealed.has(c.id)) continue;
    const src = normalizeClueForFind(c.source_text);
    if (!src) continue;
    if (needle === src || needle.includes(src) || src.includes(needle)) return c;
  }
  return null;
}

/**
 * Normalisation "large" pour l'appariement au clic : trim + lower + retire
 * TOUS les espaces (aussi les `\ ` LaTeX ne comptent pas). Sert UNIQUEMENT à
 * `findClueByClick` — la comparaison stricte reste dans `normalizeClueMatch`
 * pour `findClueOccurrences` (utilisée par l'éditeur, où on ne veut pas des
 * matches trop laxistes).
 */
function normalizeClueForFind(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, '');
}

/** XP total gagné (à créditer à student_profiles.xp en fin de bloc). */
export function totalEarned(state: ClueHuntState): number {
  let sum = 0;
  for (const xp of state.earned.values()) sum += xp;
  return sum;
}

/** État initial. */
export function initialClueHuntState(): ClueHuntState {
  return { wrongClicks: 0, consecutiveWrongClicks: 0, revealed: new Set(), earned: new Map() };
}

/**
 * Transition : l'élève trouve `clue` via un clic correct. Crédite la
 * récompense EFFECTIVE (halving des mauvais clics × échelle ×10 × facteur de
 * vitesse), RESET le compteur de mauvais clics consécutifs et remet le chrono à
 * zéro (l'appelant gère le tick). `elapsedMs`/`timeoutMs` = temps écoulé depuis
 * le dernier reset du chrono (omis → aucune décote de vitesse).
 */
export function applyClueFound(
  state: ClueHuntState,
  clue: Clue,
  elapsedMs?: number,
  timeoutMs?: number,
): ClueHuntState {
  if (state.revealed.has(clue.id)) return state;
  const reward = computeClueReward(clue.xp_reward, state.wrongClicks, elapsedMs, timeoutMs);
  const revealed = new Set(state.revealed);
  revealed.add(clue.id);
  const earned = new Map(state.earned);
  earned.set(clue.id, reward);
  return {
    wrongClicks: state.wrongClicks,
    consecutiveWrongClicks: 0,
    revealed,
    earned,
  };
}

/**
 * Transition : mauvais clic. Renvoie soit le nouvel état seul, soit — si on
 * atteint `CLUE_HUNT_WRONG_CLICKS_THRESHOLD` clics consécutifs — le nouvel
 * état + l'indice révélé automatiquement (sans XP, comme un timeout). Le
 * compteur consécutif est reset après une révélation.
 */
export function applyWrongClick(
  state: ClueHuntState,
  clues: readonly Clue[],
): { state: ClueHuntState; revealed: Clue | null } {
  const nextWrong = state.wrongClicks + 1;
  const nextConsecutive = state.consecutiveWrongClicks + 1;
  if (nextConsecutive >= CLUE_HUNT_WRONG_CLICKS_THRESHOLD) {
    const next = nextClueOnTimeout(clues, state.revealed);
    if (next) {
      const revealed = new Set(state.revealed);
      revealed.add(next.id);
      const earned = new Map(state.earned);
      earned.set(next.id, 0);
      return {
        state: { wrongClicks: nextWrong, consecutiveWrongClicks: 0, revealed, earned },
        revealed: next,
      };
    }
  }
  return {
    state: {
      wrongClicks: nextWrong,
      consecutiveWrongClicks: nextConsecutive,
      revealed: new Set(state.revealed),
      earned: new Map(state.earned),
    },
    revealed: null,
  };
}

/**
 * Transition : timeout — révèle l'indice suivant SANS XP (0), redémarre le
 * chrono côté appelant. Renvoie l'état et l'indice révélé (ou null si fini).
 */
export function applyTimeout(
  state: ClueHuntState,
  clues: readonly Clue[],
): { state: ClueHuntState; revealed: Clue | null } {
  const next = nextClueOnTimeout(clues, state.revealed);
  if (!next) return { state, revealed: null };
  const revealed = new Set(state.revealed);
  revealed.add(next.id);
  const earned = new Map(state.earned);
  earned.set(next.id, 0);
  return {
    state: {
      wrongClicks: state.wrongClicks,
      consecutiveWrongClicks: 0,
      revealed,
      earned,
    },
    revealed: next,
  };
}

/** True si tous les indices ont été révélés (fin du bloc). */
export function isClueHuntDone(state: ClueHuntState, clues: readonly Clue[]): boolean {
  if (clues.length === 0) return true;
  for (const c of clues) if (!state.revealed.has(c.id)) return false;
  return true;
}

/** Défaut applicatif du chrono (aligné sur ClueHuntDataSchema). */
export const CLUE_HUNT_DEFAULT_TIMEOUT_MS = 30_000;
